package ai.rules

/** How much of the flow tree to draw at a given widget size. */
data class SankeyDensity(
    /** 2 = Income→buckets, 3 = +primaries, 4 = +sub-categories. */
    val levels: Int,
    /** Categories drawn per bucket before the remainder collapses to "Other". */
    val strandsPerBucket: Int,
)

/**
 * Derives drawing density and the per-level horizontal geometry from the
 * available pixels. The flow runs left to right, so the axes are read
 * independently: width drives LEVELS and their widths, height drives how many
 * STRANDS stay legible (ribbon thickness is vertical).
 *
 * **Horizontal shrink compresses before it drops.** The income→buckets span is
 * just a fan carrying a 3–4 way split — it needs almost no width — so it is the
 * first to compress. The leaf level is only dropped when every span, including
 * that blob, is already at its minimum. So narrowing the widget squeezes the
 * columns together and keeps the sub-categories, rather than deleting the
 * leaves while a wide, near-empty blob sits on the left.
 *
 * **Vertical shrink slims the bands.** More strands are kept as the widget gets
 * shorter (MIN_STRAND_PX is small), so a short widget shows thin ribbons at full
 * width rather than dropping categories.
 */
object SankeyLayout {

    /** Thinner than this and a ribbon can't be read or clicked. Small on purpose:
     *  a short widget keeps its leaves and just draws them slimmer. */
    const val MIN_STRAND_PX = 10

    /**
     * Width set aside on the right for the last level's labels, which have no
     * level after them to sit in. Sized against French ("Loyer et services
     * publics"); longer names are truncated rather than allowed past the edge.
     */
    const val LABEL_GUTTER_PX = 116

    /** Fixed horizontal chrome SankeyChart draws around the spans: 2×PAD + the income node. */
    const val CHROME_PX = 38

    /** The income→buckets span is just a fan; it can compress to this before anything else does. */
    const val SPAN_MIN_BLOB = 26
    /** …and never hogs more than this on a wide widget, so it can't dominate. */
    const val SPAN_MAX_BLOB = 110
    /** A labelled span (its destination nodes carry category text) needs at least this. */
    const val SPAN_MIN_LABELED = 74
    /** …and reads comfortably at this. */
    const val SPAN_IDEAL_LABELED = 150

    const val MIN_LEVELS = 2
    const val MAX_LEVELS = 4

    /** Never draw zero categories, never turn a huge widget into spaghetti. */
    const val MIN_STRANDS_PER_BUCKET = 1
    const val MAX_STRANDS_PER_BUCKET = 6

    /** Width available to the (levels − 1) spans, after chrome and the label gutter. */
    fun usableSpanWidth(widthPx: Int): Int =
        (widthPx - CHROME_PX - LABEL_GUTTER_PX).coerceAtLeast(0)

    fun densityFor(availableWidthPx: Int, availableHeightPx: Int): SankeyDensity {
        val usable = usableSpanWidth(availableWidthPx)
        // Keep the leaf level as long as every span can still reach its minimum.
        val levels = when {
            usable >= SPAN_MIN_BLOB + SPAN_MIN_LABELED * 3 -> 4
            usable >= SPAN_MIN_BLOB + SPAN_MIN_LABELED * 2 -> 3
            else -> 2
        }.coerceIn(MIN_LEVELS, MAX_LEVELS)
        val strands = (availableHeightPx / MIN_STRAND_PX)
            .coerceIn(MIN_STRANDS_PER_BUCKET, MAX_STRANDS_PER_BUCKET)
        return SankeyDensity(levels = levels, strandsPerBucket = strands)
    }

    /**
     * Per-span pixel widths for [levels], distributing [usable] px, blob first.
     * The blob span absorbs shrink before the labelled spans do: labelled spans
     * hold their ideal until the blob is at its minimum, then they compress too;
     * on a wide widget the blob is capped so the extra room goes to the labels.
     */
    fun spanWidths(levels: Int, usable: Int): List<Double> {
        val spans = (levels - 1).coerceAtLeast(1)
        if (spans == 1) return listOf(usable.toDouble()) // 2 levels: a single blob span
        val labeled = spans - 1
        var labeledW = SPAN_IDEAL_LABELED.toDouble()
        var blobW = usable - labeledW * labeled
        when {
            blobW > SPAN_MAX_BLOB -> { // wide widget: cap the blob, give the slack to labels
                blobW = SPAN_MAX_BLOB.toDouble()
                labeledW = (usable - blobW) / labeled
            }
            blobW < SPAN_MIN_BLOB -> { // tight widget: blob at min, labels compress
                blobW = SPAN_MIN_BLOB.toDouble()
                labeledW = ((usable - blobW) / labeled).coerceAtLeast(SPAN_MIN_LABELED.toDouble())
            }
        }
        return listOf(blobW) + List(labeled) { labeledW }
    }
}
